import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { validateObject, when } from '@/utils/validx-util'
import type {
  TaxRefundDeliveryMethod,
  TaxRefundDeliveryMethodType,
} from './TaxRefundDeliveryMethod'
import type {
  TaxRefundMethodVersion,
  TaxRefundType,
} from './TaxRefundMethodVersion'
import { CheckDeliveryMethodEditorViewModel } from './CheckDeliveryMethodEditorViewModel'
import { DirectDepositDeliveryMethodEditorViewModel } from './DirectDepositDeliveryMethodEditorViewModel'
import { FutureTaxCreditDeliveryMethodEditorViewModel } from './FutureTaxCreditDeliveryMethodEditorViewModel'

/**
 * Editor for a tax refund method.
 */
export class TaxRefundMethodEditorViewModel {
  readonly directDepositVm = new DirectDepositDeliveryMethodEditorViewModel()
  readonly checkVm = new CheckDeliveryMethodEditorViewModel()
  readonly futureTaxCreditVm =
    new FutureTaxCreditDeliveryMethodEditorViewModel()

  /**
   * The selected delivery method, which picks the sub-editor to delegate to.
   */
  @observable accessor selectedMethod: TaxRefundDeliveryMethodType | null = null

  readonly validation = validationContext(this, {
    selectedMethod: [required('Please choose a refund method')],
    directDepositVm: [
      when(() => this.selectedMethod === 'DirectDeposit', validateObject()),
    ],
    checkVm: [when(() => this.selectedMethod === 'Check', validateObject())],
    futureTaxCreditVm: [
      when(() => this.selectedMethod === 'FutureTaxCredit', validateObject()),
    ],
  })

  constructor(readonly refundType: TaxRefundType) {}

  /**
   * Delivery methods available for the refund type.
   */
  @computed get availableMethods(): readonly TaxRefundDeliveryMethodType[] {
    return this.refundType === 'Business'
      ? ['DirectDeposit', 'Check', 'FutureTaxCredit']
      : ['DirectDeposit', 'Check']
  }

  /**
   * The error for when no method has been selected.
   */
  @computed get selectionError() {
    return this.validation.getError('selectedMethod') ?? null
  }

  /**
   * Selects a delivery method.
   */
  @action.bound
  selectMethod(method: TaxRefundDeliveryMethodType) {
    this.selectedMethod = method
    this.validation.clearErrors('selectedMethod')
  }

  /**
   * Resets the editor to the given method, or to defaults.
   */
  @action.bound
  reset(method?: TaxRefundMethodVersion | null) {
    const deliveryMethod = method?.deliveryMethod ?? null
    this.selectedMethod = deliveryMethod?.type ?? null
    this.directDepositVm.reset(
      deliveryMethod?.type === 'DirectDeposit' ? deliveryMethod : undefined,
    )
    this.checkVm.reset(
      deliveryMethod?.type === 'Check' ? deliveryMethod : undefined,
    )
    this.validation.reset()
  }

  /**
   * Maps the selected sub-editor to {@link TaxRefundDeliveryMethod}.
   */
  toDeliveryMethod(): TaxRefundDeliveryMethod {
    switch (this.selectedMethod) {
      case 'DirectDeposit':
        return this.directDepositVm.toDeliveryMethod()
      case 'Check':
        return this.checkVm.toDeliveryMethod()
      case 'FutureTaxCredit':
        return this.futureTaxCreditVm.toDeliveryMethod()
      case null:
        throw new Error('No delivery method selected')
    }
  }
}
